import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Option {
  id: string;
  label: string;
}

interface RecordingsFilterBarProps {
  events: Option[];
  sessions: Option[];
  drivers: Option[];
}

export default function RecordingsFilterBar({
  events,
  sessions,
  drivers,
}: RecordingsFilterBarProps) {
  const { t } = useTranslation("pages");
  const [searchParams, setSearchParams] = useSearchParams();

  const setParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        if (value) prev.set(key, value);
        else prev.delete(key);
        prev.delete("offset");
        return prev;
      },
      { replace: true },
    );
  };

  const selectClass =
    "rounded-md border border-secondary-300 bg-white px-2 py-1.5 text-sm";

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <select
        aria-label={t("teams.recordings.event")}
        className={selectClass}
        value={searchParams.get("eventId") ?? ""}
        onChange={(e) => setParam("eventId", e.target.value)}
      >
        <option value="">{t("teams.recordings.allEvents")}</option>
        {events.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>

      <select
        aria-label={t("teams.recordings.session")}
        className={selectClass}
        value={searchParams.get("sessionId") ?? ""}
        onChange={(e) => setParam("sessionId", e.target.value)}
      >
        <option value="">{t("teams.recordings.allSessions")}</option>
        {sessions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>

      <select
        aria-label={t("teams.recordings.driver")}
        className={selectClass}
        value={searchParams.get("driverId") ?? ""}
        onChange={(e) => setParam("driverId", e.target.value)}
      >
        <option value="">{t("teams.recordings.allDrivers")}</option>
        {drivers.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>

      <label className="flex items-center gap-1 text-sm text-secondary-600">
        {t("teams.recordings.from")}
        <input
          type="date"
          className={selectClass}
          value={searchParams.get("from") ?? ""}
          onChange={(e) => setParam("from", e.target.value)}
        />
      </label>
      <label className="flex items-center gap-1 text-sm text-secondary-600">
        {t("teams.recordings.to")}
        <input
          type="date"
          className={selectClass}
          value={searchParams.get("to") ?? ""}
          onChange={(e) => setParam("to", e.target.value)}
        />
      </label>
    </div>
  );
}
